import { BarcodeApiClient } from '@jbhi-fi/apis/src/barcode/BarcodeApiClient';
import { serviceHelper } from '../../config/service-helper';
import { Coupon, WalletCoupon } from '@jbhi-fi/components/src/types';
import { ProductExclusions, CartCoupon } from './types';
import { OnlineCouponsApiClient } from '@jbhi-fi/apis/src/onlineCouponsApi/OnlineCouponsApiClient';
import { Exclusions } from '@jbhi-fi/apis/src/onlineCouponsApi/types';

const country = window.Jbhifi.storeLocation;
const barcodeApiClient = new BarcodeApiClient(`${serviceHelper.themeApisUrl}/barcode`);
const couponApiClient = new OnlineCouponsApiClient({
  baseUrl: `${serviceHelper.themeApisUrl}/coupons`,
  countryCode: country,
});

const getValidWalletCouponRefs = (couponsInWallet?: Array<WalletCoupon>) => {
  if (!Array.isArray(couponsInWallet) || !couponsInWallet.length) return null;
  return new Set(couponsInWallet.filter(w => w.CouldBeApplied).map(w => w.Reference));
};

const getFilteredCoupons = (
  coupons: Array<Coupon>,
  appliedCoupons: Array<CartCoupon>,
  couponsInWallet?: Array<WalletCoupon>
) => {
  const today = new Date().setHours(0, 0, 0, 0);
  const validWalletCouponRefs = getValidWalletCouponRefs(couponsInWallet);

  return (
    coupons
      ?.filter(coupon => {
        const isValid = !coupon.redemptionDate && new Date(coupon.expiryDate).setHours(0, 0, 0, 0) > today;
        if (validWalletCouponRefs) {
          return isValid && validWalletCouponRefs.has(coupon.couponCode);
        }
        return isValid;
      })
      .map(coupon => {
        const appliedCoupon = appliedCoupons?.find(c => c.code === coupon.couponCode);
        return {
          ...coupon,
          isApplied: Boolean(appliedCoupon),
          success: Boolean(appliedCoupon?.success),
        };
      }) || []
  );
};

const handleExclusions = async (coupons: Array<Coupon>, product?: ProductExclusions): Promise<Array<Coupon>> => {
  if (!product) return coupons;
  const externalIds = coupons.map(coupon => coupon.couponExternalId.toString());
  const exclusionsPromises = externalIds.map(async externalId => {
    try {
      const response = await couponApiClient.getExclusions(externalId);
      return response.data;
    } catch (error) {
      console.error('Error finding exclusions for coupon:', error);
      return undefined;
    }
  });
  const exclusions = (await Promise.all(exclusionsPromises)).filter(Boolean) as Array<Exclusions>;
  return coupons.filter((coupon: Coupon) => {
    const exclusion = exclusions.find(e => e.externalId === coupon.couponExternalId.toString());
    if (!exclusion) return false;

    const hasMatchingCondition = (conditionType: 'include' | 'exclude', identifierType: string, value: string) =>
      exclusion.redemptionConditions.some(
        condition =>
          condition.conditionType.toLowerCase() === conditionType.toLowerCase() &&
          condition.identifierType === identifierType &&
          condition.identifier.includes(value.toString())
      );

    const isDepartmentCodeIncluded = hasMatchingCondition('include', 'DepartmentCode', product.departmentCode);

    const isSupplierExcluded = hasMatchingCondition('exclude', 'SupplierNumber', product.supplierNumber);

    const isSkuExcluded = hasMatchingCondition('exclude', 'Sku', product.sku);

    const isProductGroupExcluded = hasMatchingCondition('exclude', 'ProductGroupId', product.productGroupId);

    const isPreOrderOrMarketplace = product.isPreOrder || product.isMarketplace;

    const isExcluded =
      isPreOrderOrMarketplace ||
      isSupplierExcluded ||
      isSkuExcluded ||
      isProductGroupExcluded ||
      !isDepartmentCodeIncluded;

    return !(isExcluded && coupon.couponReference.toLowerCase().includes('perks'));
  });
};
const generateCouponBarcodes = async (couponsInput: Array<Coupon>) => {
  const newBarcodeMap = new Map<string, string | undefined>();
  const barcodePromises = couponsInput.map(async coupon => {
    try {
      const formattedCode = `#${coupon.couponCode}`;
      const response = await barcodeApiClient.generateBarcode({ couponCode: formattedCode });
      const barcodeBase64 = response?.data?.base64;
      newBarcodeMap.set(coupon.couponCode, barcodeBase64);
    } catch (error) {
      console.error('Error generating barcode for coupon:', coupon.couponCode, error);
    }
  });
  await Promise.all(barcodePromises);

  return newBarcodeMap;
};

const sortApplicableCouponsByDiscount = (coupons: Array<Coupon>, couponsInWallet: Array<WalletCoupon>) => {
  if (!Array.isArray(couponsInWallet) || couponsInWallet.length === 0) return coupons;
  const validWalletCouponsMap = new Map(couponsInWallet.map(w => [w.Reference, w.DiscountInc ?? 0]));

  const enrichedCoupons = coupons.map(coupon => ({
    ...coupon,
    discount: validWalletCouponsMap.get(coupon.couponCode) || 0,
  }));

  return enrichedCoupons
    .sort((a, b) => b.discount - a.discount)
    .map((coupon, index) => ({
      ...coupon,
      isBestDeal: index === 0,
    }));
};

const mapCartCouponToCoupon = (
  appliedCoupons: Array<CartCoupon>,
  couponHandles: Array<string | undefined>,
  isLoggedIn: boolean
) => {
  const couponsToMap =
    isLoggedIn && couponHandles.length > 0
      ? appliedCoupons.filter(coupon => coupon.action === 'manual' && !couponHandles.includes(coupon.coupon))
      : appliedCoupons;

  return couponsToMap.map(coupon => ({
    ...(coupon.name && { couponName: coupon.name }),
    ...(coupon.coupon && { couponReference: coupon.coupon }),
    ...(coupon.message && { couponDescription: coupon.message }),
    ...(coupon.code && { couponCode: coupon.code }),
    ...(coupon.success !== undefined && { success: coupon.success }),
    ...(coupon.amount !== undefined && { discount: coupon.amount }),
    ...(coupon.externalId !== undefined && { couponExternalId: coupon.externalId }),
    content: {
      ...(coupon.name && { displayName: coupon.name }),
      termsAndConditions: null,
    },
    allocatedDate: '',
    expiryDate: '',
    isApplied: true,
    isBestDeal: false,
  }));
};

export {
  generateCouponBarcodes,
  getFilteredCoupons,
  handleExclusions,
  sortApplicableCouponsByDiscount,
  mapCartCouponToCoupon,
};
